import { createSearchParams, generatePath, Link } from "react-router-dom"
import { Tag, Typography } from "antd"
import { ColumnsType } from "antd/es/table"
import { choose } from "choose-matching"

import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { paths } from "@src/app/paths"
import { router } from "@src/pages"
import { TransactionsTab } from "@src/pages/transactions"
import {
  RefundResponse,
  RefundResponseFeePayerEnum,
  RefundResponseStatusEnum,
} from "@src/shared/api/generated"
import { DateTimeTableCell } from "@src/shared/componemts/DateTimeTableCell"
import {
  filtersPresets,
  getFilterSimpleFiled,
} from "@src/shared/componemts/TableFilter/classes/constants"
import { WithCopy } from "@src/shared/componemts/WithCopy"
import { paddingLimit } from "@src/shared/strings"

export const RefundsColumns: ColumnsType<RefundResponse> = [
  {
    dataIndex: "id",
    render: (_, { id }) => (
      <WithCopy
        content={id!}
        view={<Typography.Text>{paddingLimit(id!, 0, 3)}</Typography.Text>}
      />
    ),
    title: "ID",
  },
  {
    render: (_, { payment_id }) =>
      choose(
        [
          Boolean(payment_id),
          <WithCopy
            content={payment_id!}
            view={
              <Typography.Link
                onClick={() =>
                  router.navigate({
                    pathname: paths.transactions,
                    search: `?${createSearchParams({ id: payment_id!, tab: TransactionsTab.Payments })}`,
                  })
                }
              >
                {paddingLimit(payment_id!, 0, 3)}
              </Typography.Link>
            }
          />,
        ],
        [!payment_id, <span>"-"</span>],
      ),
    title: "Payment ID",
  },
  {
    render: (_, { charge_id }) =>
      choose(
        [
          Boolean(charge_id),
          <WithCopy
            content={charge_id!}
            view={
              <Typography.Text>
                {paddingLimit(charge_id!, 0, 3)}
              </Typography.Text>
            }
          />,
        ],
        [!charge_id, <span>"-"</span>],
      ),
    title: "Charge ID",
  },
  {
    dataIndex: "address_to",
    render: (_, { address_to }) => {
      return choose(
        [
          Boolean(address_to),
          <WithCopy
            content={address_to!}
            view={
              <Typography.Text>
                {paddingLimit(address_to!, 0, 3)}
              </Typography.Text>
            }
          />,
        ],
        [!address_to, <span>"-"</span>],
      )
    },
    title: "Address to",
  },
  {
    render: (_, { amount }) => {
      return (
        <Typography.Text style={{ fontWeight: 700 }}>{amount}</Typography.Text>
      )
    },
    title: "Amount",
  },
  {
    render: (_, { network, symbol }) => {
      const currency = GlobalCurrenciesCollection().getBySymbolNetwork({
        network: network!,
        symbol: symbol!,
      })

      return (
        <Typography.Text>{currency?.symbol_alias ?? symbol}</Typography.Text>
      )
    },
    title: "Symbol",
  },
  {
    render: (_, { network }) => {
      return <Typography.Text>{network}</Typography.Text>
    },
    title: "Network",
  },
  {
    render: (_, { status }) => {
      const cMap = {
        [RefundResponseStatusEnum.Completed]: "green",
        [RefundResponseStatusEnum.Failed]: "error",
        [RefundResponseStatusEnum.Pending]: "geekblue",
      }

      return <Tag color={cMap[status!] ?? "geekblue"}>{status}</Tag>
    },
    title: "Status",
  },
  {
    dataIndex: "fee_payer",
    render: (_, { fee_payer }) => {
      return <Typography.Text>{fee_payer}</Typography.Text>
    },
    title: "Fee payer",
  },
  {
    dataIndex: "created_at",
    render: (_, { created_at }) => <DateTimeTableCell value={created_at} />,
    title: "Created At",
    width: 120,
  },
]

export const RefundsFilter = [
  {
    ...getFilterSimpleFiled("paymentId").search({
      col: 6,
      label: "Payment ID",
      placeholder: "Search by payment ID",
    }),
  },
  { ...filtersPresets.order, col: 6 },
  { ...filtersPresets.status, col: 6 },
]
